import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { joinCss } from '@/shared/lib/css-utils';
import css from './range-calendar.module.scss';

// Monday-first week, matching the rest of the app's calendars. The label
// is the first letter; the full name keys the list (the two "T"/"S" days
// would collide on a single-letter key).
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

const MONTHS_FULL = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const DAY_MS = 86_400_000;

// `value` ISO strings are UTC wall-clock (start = `…T00:00:00Z`, end =
// `…T23:59:59Z`); the leading `YYYY-MM-DD` is the day key, and those keys
// sort/compare lexically so range membership is a string comparison.
const isoDayKey = (iso?: string) => (iso ? iso.slice(0, 10) : undefined);
const dayKeyOf = (date: Date) => date.toISOString().slice(0, 10);
const monthKeyOf = (iso?: string) =>
  (iso ?? new Date().toISOString()).slice(0, 7);

type RangeCalendarProps = {
  startDate?: string;
  endDate?: string;
  // Fires the clicked day as a `YYYY-MM-DD` key; the picker decides what
  // the click means (move the window, set an endpoint, …).
  onSelectDay: (dayKey: string) => void;
};

// Inline month grid that highlights the whole selected span (start →
// in-between → end) so the trip's duration is visible at a glance — the
// native `<input type="date">` calendar can only mark a single day.
export const RangeCalendar = ({
  startDate,
  endDate,
  onSelectDay,
}: RangeCalendarProps) => {
  const [monthKey, setMonthKey] = useState(() =>
    monthKeyOf(startDate ?? endDate)
  );

  // Follow the start as it moves (e.g. the fixed-duration window jumps to
  // another month) without trapping manual prev/next navigation — this
  // only re-runs when the start day actually changes.
  useEffect(() => {
    if (startDate) setMonthKey(startDate.slice(0, 7));
  }, [startDate]);

  const [yearStr, monthStr] = monthKey.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr) - 1; // 0-based

  const firstOfMonth = new Date(Date.UTC(year, month, 1));
  // getUTCDay: 0=Sun…6=Sat → shift so Monday is column 0.
  const weekdayOffset = (firstOfMonth.getUTCDay() + 6) % 7;
  const firstCell = new Date(firstOfMonth.getTime() - weekdayOffset * DAY_MS);

  const startKey = isoDayKey(startDate);
  const endKey = isoDayKey(endDate);
  const todayKey = new Date().toISOString().slice(0, 10);

  // Six weeks always render so the grid height never jumps between months.
  const cells = Array.from({ length: 42 }, (_, i) => {
    const date = new Date(firstCell.getTime() + i * DAY_MS);
    const key = dayKeyOf(date);
    return {
      key,
      day: date.getUTCDate(),
      inCurrentMonth: date.getUTCMonth() === month,
      isToday: key === todayKey,
      isStart: !!startKey && key === startKey,
      isEnd: !!endKey && key === endKey,
      inRange: !!startKey && !!endKey && key >= startKey && key <= endKey,
    };
  });

  const shiftMonth = (delta: number) => {
    const next = new Date(Date.UTC(year, month + delta, 1));
    setMonthKey(next.toISOString().slice(0, 7));
  };

  return (
    <div className={css.calendar}>
      <div className={css.header}>
        <button
          aria-label="Previous month"
          className={css.nav}
          onClick={() => shiftMonth(-1)}
          type="button"
        >
          <ChevronLeft size={16} />
        </button>
        <span className={css.monthLabel}>
          {MONTHS_FULL[month]} {year}
        </span>
        <button
          aria-label="Next month"
          className={css.nav}
          onClick={() => shiftMonth(1)}
          type="button"
        >
          <ChevronRight size={16} />
        </button>
      </div>
      <div className={css.weekdays}>
        {WEEKDAYS.map((name) => (
          <span className={css.weekday} key={name}>
            {name[0]}
          </span>
        ))}
      </div>
      <div className={css.grid}>
        {cells.map((cell) => {
          const single = cell.isStart && cell.isEnd;
          return (
            <button
              aria-label={cell.key}
              aria-pressed={cell.inRange}
              className={joinCss(
                css.day,
                !cell.inCurrentMonth && css.dayMuted,
                cell.inRange && css.dayInRange,
                cell.isStart && !single && css.dayStart,
                cell.isEnd && !single && css.dayEnd,
                single && css.daySingle,
                cell.isToday && css.dayToday
              )}
              key={cell.key}
              onClick={() => onSelectDay(cell.key)}
              type="button"
            >
              {cell.day}
            </button>
          );
        })}
      </div>
    </div>
  );
};
